import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sparkles, CheckCircle2, XCircle, TrendingUp, MessageSquare, Book, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminContentSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchSuggestions();
  }, [filter]);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/support/chat/suggestions?status=${filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions', error);
      toast.error('Fehler beim Laden der Vorschläge');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (suggestionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/chat/suggestions/${suggestionId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vorschlag genehmigt!');
      fetchSuggestions();
    } catch (error) {
      toast.error('Fehler beim Genehmigen');
    }
  };

  const handleReject = async (suggestionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/chat/suggestions/${suggestionId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vorschlag abgelehnt');
      fetchSuggestions();
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    }
  };

  const getContextIcon = (contextType) => {
    return contextType === 'manual' ? <Book className="w-4 h-4" /> : <HelpCircle className="w-4 h-4" />;
  };

  const getContextColor = (contextType) => {
    return contextType === 'manual' ? '#1EA0E6' : '#F59E0B';
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Content-Vorschläge aus KI-Chat
          </h2>
          <p style={{ color: '#94A3B8' }}>
            Vorschläge für neue Einträge im Benutzerhandbuch und FAQ, die von der KI automatisch erkannt wurden
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'rejected'].map(status => (
            <Button
              key={status}
              onClick={() => setFilter(status)}
              variant={filter === status ? 'default' : 'outline'}
              style={{
                backgroundColor: filter === status ? '#1EA0E6' : 'transparent',
                borderColor: '#1F2937',
                color: filter === status ? '#FFFFFF' : '#94A3B8'
              }}
            >
              {status === 'pending' && 'Ausstehend'}
              {status === 'approved' && 'Genehmigt'}
              {status === 'rejected' && 'Abgelehnt'}
            </Button>
          ))}
        </div>

        {/* Suggestions List */}
        {loading ? (
          <p style={{ color: '#94A3B8' }}>Lädt Vorschläge...</p>
        ) : suggestions.length === 0 ? (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#94A3B8' }} />
              <p style={{ color: '#94A3B8' }}>Keine Vorschläge mit Status "{filter}"</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <Card
                key={index}
                style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="p-1 rounded"
                          style={{ backgroundColor: `${getContextColor(suggestion.context_type)}20` }}
                        >
                          {getContextIcon(suggestion.context_type)}
                        </div>
                        <Badge
                          variant="secondary"
                          style={{
                            backgroundColor: `${getContextColor(suggestion.context_type)}20`,
                            color: getContextColor(suggestion.context_type)
                          }}
                        >
                          {suggestion.context_type === 'manual' ? 'Benutzerhandbuch' : 'FAQ'}
                        </Badge>
                        {suggestion.votes > 1 && (
                          <div className="flex items-center gap-1 text-xs" style={{ color: '#F59E0B' }}>
                            <TrendingUp className="w-3 h-3" />
                            {suggestion.votes}× gefragt
                          </div>
                        )}
                      </div>
                      <CardTitle style={{ color: '#E6EDF3', fontSize: '1.25rem' }}>
                        {suggestion.suggested_title}
                      </CardTitle>
                    </div>
                    {suggestion.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(suggestion.id)}
                          style={{ backgroundColor: '#22C55E', color: '#FFFFFF' }}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Genehmigen
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleReject(suggestion.id)}
                          variant="outline"
                          style={{ borderColor: '#EF4444', color: '#EF4444' }}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Ablehnen
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Original User Question */}
                  <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#0B0F14' }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                      <MessageSquare className="w-3 h-3 inline mr-1" />
                      Ursprüngliche Frage:
                    </p>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      "{suggestion.user_question}"
                    </p>
                  </div>

                  {/* Suggested Content */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold mb-1" style={{ color: '#E6EDF3' }}>
                        Kategorie:
                      </p>
                      <Badge variant="outline">{suggestion.suggested_category}</Badge>
                    </div>
                    <div>
                      <p className="text-xs font-semibold mb-2" style={{ color: '#E6EDF3' }}>
                        Vorgeschlagener Inhalt:
                      </p>
                      <div className="p-3 rounded" style={{ backgroundColor: '#1F2937' }}>
                        <p className="text-sm whitespace-pre-wrap" style={{ color: '#94A3B8' }}>
                          {suggestion.suggested_content}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="mt-4 pt-4 border-t flex items-center gap-4 text-xs" style={{ borderColor: '#1F2937', color: '#94A3B8' }}>
                    <span>
                      Erstellt: {new Date(suggestion.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </span>
                    {suggestion.approved_at && (
                      <span style={{ color: '#22C55E' }}>
                        ✓ Genehmigt: {new Date(suggestion.approved_at).toLocaleDateString('de-DE')}
                      </span>
                    )}
                    {suggestion.rejected_at && (
                      <span style={{ color: '#EF4444' }}>
                        ✗ Abgelehnt: {new Date(suggestion.rejected_at).toLocaleDateString('de-DE')}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
